import { DeviceInfo } from '../../common/enums'
import {
  FleetSignalRow,
  SENSITIVITY_DBM,
  SIGNAL_QUALITY_LABELS,
  SIGNAL_QUALITY_TONES,
  SignalQuality,
} from '../../types/signalStrength'

/**
 * The fleet overview's weak-signal list.
 *
 * Built for "which install do I go and fix first": worst first, named the way
 * the rest of the dashboard names devices, and with margin - the one number
 * that decides whether messages get through - shown as a bar against the
 * Sigfox floor rather than buried in a string of dBm figures. The raw median
 * stays visible, smaller, for whoever wants it.
 */

/** Margin, in dB, at which the bar is full. Above this a link is comfortably clear. */
const BAR_FULL_DB = 15

const SEVERITY: Record<SignalQuality, number> = { critical: 0, weak: 1, fair: 2, good: 3, unknown: 4 }

const BAR_TONES: Record<SignalQuality, string> = {
  critical: 'bg-red-500',
  weak: 'bg-amber-500',
  fair: 'bg-blue-500',
  good: 'bg-green-600',
  unknown: 'bg-gray-300',
}

/** Worst first: band, then least margin, then single-station before multi. */
export function sortWeakSignal(rows: FleetSignalRow[]): FleetSignalRow[] {
  return [...rows].sort(
    (a, b) =>
      SEVERITY[a.summary.quality] - SEVERITY[b.summary.quality] ||
      (a.summary.marginDb ?? Infinity) - (b.summary.marginDb ?? Infinity) ||
      a.summary.stations.length - b.summary.stations.length ||
      a.sigfoxId.localeCompare(b.sigfoxId)
  )
}

export function marginLabel(marginDb: number | null): string {
  if (marginDb === null) return 'No data'
  const db = Math.round(marginDb)
  if (db === 0) return 'At the floor'
  return db > 0 ? `${db} dB above floor` : `${-db} dB below floor`
}

function timeAgo(iso: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  if (isNaN(ms)) return '—'
  const hours = Math.floor(ms / 3_600_000)
  if (hours < 1) return 'under 1h ago'
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

function Headroom({ marginDb, quality, rssiMedian }: { marginDb: number | null; quality: SignalQuality; rssiMedian: number | null }) {
  const fill = marginDb === null ? 0 : Math.max(0, Math.min(1, marginDb / BAR_FULL_DB))
  return (
    <div className="w-full sm:w-44">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm text-gray-800">{marginLabel(marginDb)}</span>
        {rssiMedian !== null && <span className="text-[11px] text-gray-400">{rssiMedian} dBm</span>}
      </div>
      <div aria-hidden="true" className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-100">
        {/* A sliver even at zero, so a link sitting on the floor still reads as a bar. */}
        <div className={`h-full rounded-full ${BAR_TONES[quality]}`} style={{ width: `${Math.max(fill * 100, 3)}%` }} />
      </div>
    </div>
  )
}

export default function WeakSignalList({
  rows,
  devices,
  showAccount,
  onOpen,
}: {
  rows: FleetSignalRow[]
  /** Devices by Sigfox id, for names. A row with no match falls back to its id. */
  devices: Record<string, DeviceInfo>
  showAccount: boolean
  onOpen: (sigfoxId: string) => void
}) {
  const sorted = sortWeakSignal(rows)

  return (
    <div>
      <div className="hidden px-2 pb-2 text-[11px] font-medium uppercase tracking-wide text-gray-400 sm:flex sm:items-center sm:gap-4">
        <span className="flex-1">Device</span>
        <span className="w-44">Signal margin</span>
        <span className="w-24">Base stations</span>
        <span className="w-20">Last heard</span>
        <span className="w-16 text-right">Rating</span>
      </div>
      <ul className="divide-y divide-gray-100 border-t border-gray-100">
        {sorted.map(({ sigfoxId, summary }) => {
          const device = devices[sigfoxId]
          const singleStation = summary.stations.length === 1
          return (
            <li key={sigfoxId}>
              <button
                type="button"
                onClick={() => onOpen(sigfoxId)}
                className="flex w-full flex-col gap-2 rounded px-2 py-2.5 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 sm:flex-row sm:items-center sm:gap-4"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-gray-900">
                    {device?.deviceName || sigfoxId}
                    {device?.siteName && <span className="ml-2 font-normal text-gray-500">{device.siteName}</span>}
                  </span>
                  <span className="block truncate text-xs text-gray-400">
                    {sigfoxId}
                    {showAccount && device?.accountName && ` · ${device.accountName}`}
                  </span>
                </span>
                <Headroom marginDb={summary.marginDb} quality={summary.quality} rssiMedian={summary.rssiMedian} />
                <span className="flex items-center gap-4 sm:contents">
                  <span className={`text-xs sm:w-24 ${singleStation ? 'font-medium text-amber-700' : 'text-gray-600'}`}>
                    {summary.stations.length === 0
                      ? 'None'
                      : singleStation
                        ? '1 · no backup'
                        : `${summary.stations.length} stations`}
                  </span>
                  <span className="text-xs text-gray-600 sm:w-20">{timeAgo(summary.lastAt)}</span>
                  <span className="ml-auto sm:ml-0 sm:w-16 sm:text-right">
                    <span className={`rounded px-2 py-0.5 text-xs font-medium ${SIGNAL_QUALITY_TONES[summary.quality]}`}>
                      {SIGNAL_QUALITY_LABELS[summary.quality]}
                    </span>
                  </span>
                </span>
              </button>
            </li>
          )
        })}
      </ul>
      <p className="mt-3 text-xs text-gray-500">
        Margin is the median signal over the window, measured against {SENSITIVITY_DBM} dBm, below which Sigfox can't
        hear a device. A device heard by only one base station has no backup if that station goes down.
      </p>
    </div>
  )
}
